import {
	DEFAULT_TENANT,
	getTenant,
	getTenantByDomain,
	isTenantKey,
	type TenantTheme,
} from "./tenants";

const TENANT_OVERRIDE_KEY = "origo_tenant";

/**
 * Resolves the tenant for this page load, in order:
 *
 *  1. `?tenant=<key>` in the URL - for previewing another tenant. It is
 *     remembered for the rest of the browser session.
 *  2. `VITE_TENANT` - handy when running `npm run dev` against one tenant.
 *  3. what `server.js` resolved from the Host header and injected into the
 *     page - this is the real switch in production.
 *  4. the hostname in the browser - the same answer, used by `vite dev` where
 *     index.html is served unthemed.
 *  5. {@link DEFAULT_TENANT}.
 */
function resolveTenant(): TenantTheme {
	if (typeof window === "undefined") {
		return getTenant(DEFAULT_TENANT);
	}

	const fromQuery = new URLSearchParams(window.location.search).get("tenant");

	if (isTenantKey(fromQuery)) {
		sessionStorage.setItem(TENANT_OVERRIDE_KEY, fromQuery);
		return getTenant(fromQuery);
	}

	const remembered = sessionStorage.getItem(TENANT_OVERRIDE_KEY);

	if (isTenantKey(remembered)) {
		return getTenant(remembered);
	}

	const fromEnv = import.meta.env.VITE_TENANT;

	if (isTenantKey(fromEnv)) {
		return getTenant(fromEnv);
	}

	const fromServer = window.__APP_CONFIG__?.tenant;

	if (isTenantKey(fromServer)) {
		return getTenant(fromServer);
	}

	return getTenantByDomain(window.location.hostname) ?? getTenant(DEFAULT_TENANT);
}

/**
 * The active tenant. Resolved once on load - the domain cannot change without a
 * new page load, so there is no need for this to be reactive.
 */
export const tenant = resolveTenant();

export function useTenant(): TenantTheme {
	return tenant;
}

export type { TenantTheme };
